"use client";

import { memo, useCallback, useEffect, useRef } from "react";
import { cn } from "@/lib/utils";
import { DotPattern } from "@/landing-components/ui/dot-pattern";
import { animate, motion, useScroll, useTransform } from "framer-motion";

interface GlowingEffectProps {
  blur?: number;
  inactiveZone?: number;
  proximity?: number;
  spread?: number;
  variant?: "default" | "white";
  glow?: boolean;
  className?: string;
  disabled?: boolean;
  movementDuration?: number;
  borderWidth?: number;
}
const GlowingEffect = memo(
  ({
    blur = 0,
    inactiveZone = 0.7,
    proximity = 0,
    spread = 20,
    variant = "default",
    glow = false,
    className,
    movementDuration = 2,
    borderWidth = 1,
    disabled = true,
  }: GlowingEffectProps) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const lastPosition = useRef({ x: 0, y: 0 });
    const animationFrameRef = useRef<number>(0);

    const handleMove = useCallback(
      (e?: MouseEvent | { x: number; y: number }) => {
        if (!containerRef.current) return;

        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
        }

        animationFrameRef.current = requestAnimationFrame(() => {
          const element = containerRef.current;
          if (!element) return;

          const { left, top, width, height, bottom, right } = element.getBoundingClientRect();
          
          // Performance Optimization: 
          // If the card is completely off-screen, skip all calculations
          if (bottom < 0 || top > window.innerHeight || right < 0 || left > window.innerWidth) {
            return;
          }

          const mouseX = e?.x ?? lastPosition.current.x;
          const mouseY = e?.y ?? lastPosition.current.y;

          if (e) {
            lastPosition.current = { x: mouseX, y: mouseY };
          }

          const center = [left + width * 0.5, top + height * 0.5];
          const distanceFromCenter = Math.hypot(
            mouseX - center[0],
            mouseY - center[1]
          );
          const inactiveRadius = 0.5 * Math.min(width, height) * inactiveZone;

          if (distanceFromCenter < inactiveRadius) {
            element.style.setProperty("--active", "0");
            return;
          }

          const isActive =
            mouseX > left - proximity &&
            mouseX < left + width + proximity &&
            mouseY > top - proximity &&
            mouseY < top + height + proximity;

          element.style.setProperty("--active", isActive ? "1" : "0");

          if (!isActive) return;

          const currentAngle =
            parseFloat(element.style.getPropertyValue("--start")) || 0;
          let targetAngle =
            (180 * Math.atan2(mouseY - center[1], mouseX - center[0])) /
              Math.PI +
            90;

          const angleDiff = ((targetAngle - currentAngle + 180) % 360) - 180;
          const newAngle = currentAngle + angleDiff;

          animate(currentAngle, newAngle, {
            duration: movementDuration,
            ease: [0.16, 1, 0.3, 1],
            onUpdate: (value) => {
              element.style.setProperty("--start", String(value));
            },
          });
        });
      },
      [inactiveZone, proximity, movementDuration]
    );

    useEffect(() => {
      if (disabled || !containerRef.current) return;

      const handleScroll = () => handleMove();
      const handlePointerMove = (e: PointerEvent) => handleMove(e);

      // We need global listeners for proximity (detecting mouse before it enters card)
      // and for scroll (relative position change)
      window.addEventListener("scroll", handleScroll, { passive: true });
      document.body.addEventListener("pointermove", handlePointerMove, {
        passive: true,
      });

      return () => {
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
        }
        window.removeEventListener("scroll", handleScroll);
        document.body.removeEventListener("pointermove", handlePointerMove);
      };
    }, [handleMove, disabled]);

    return (
      <>
        <div
          className={cn(
            "pointer-events-none absolute -inset-px hidden rounded-[inherit] border opacity-0 transition-opacity",
            glow && "opacity-100",
            variant === "white" && "border-white",
            disabled && "!block"
          )}
        />
        <div
          ref={containerRef}
          style={
            {
              "--blur": `${blur}px`,
              "--spread": spread,
              "--start": "0",
              "--active": "0",
              "--glowingeffect-border-width": `${borderWidth}px`,
              "--repeating-conic-gradient-times": "5",
              "--gradient":
                variant === "white"
                  ? `repeating-conic-gradient(
                  from 236.84deg at 50% 50%,
                  var(--black),
                  var(--black) calc(25% / var(--repeating-conic-gradient-times))
                )`
                  : `radial-gradient(circle, #dd7bbb 10%, #dd7bbb00 20%),
                radial-gradient(circle at 40% 40%, #d79f1e 5%, #d79f1e00 15%),
                radial-gradient(circle at 60% 60%, #5a922c 10%, #5a922c00 20%), 
                radial-gradient(circle at 40% 60%, #4c7894 10%, #4c789400 20%),
                repeating-conic-gradient(
                  from 236.84deg at 50% 50%,
                  #dd7bbb 0%,
                  #d79f1e calc(25% / var(--repeating-conic-gradient-times)),
                  #5a922c calc(50% / var(--repeating-conic-gradient-times)), 
                  #4c7894 calc(75% / var(--repeating-conic-gradient-times)),
                  #dd7bbb calc(100% / var(--repeating-conic-gradient-times))
                )`,
            } as React.CSSProperties
          }
          className={cn(
            "pointer-events-none absolute inset-0 rounded-[inherit] opacity-100 transition-opacity",
            glow && "opacity-100",
            blur > 0 && "blur-[var(--blur)] ",
            className,
            disabled && "!hidden"
          )}
        >
          <div
            className={cn(
              "glow",
              "rounded-[inherit]",
              'after:content-[""] after:rounded-[inherit] after:absolute after:inset-[calc(-1*var(--glowingeffect-border-width))]',
              "after:[border:var(--glowingeffect-border-width)_solid_transparent]",
              "after:[background:var(--gradient)] after:[background-attachment:fixed]",
              "after:opacity-[var(--active)] after:transition-opacity after:duration-300",
              "after:[mask-clip:padding-box,border-box]",
              "after:[mask-composite:intersect]",
              "after:[mask-image:linear-gradient(#0000,#0000),conic-gradient(from_calc((var(--start)-var(--spread))*1deg),#00000000_0deg,#fff,#00000000_calc(var(--spread)*2deg))]"
            )}
          />
        </div>
      </>
    );
  }
);

import { Box, Lock, Search, Settings, Sparkles } from "lucide-react";

export function FeaturesSection() {
  const containerRef = useRef<HTMLDivElement>(null);
  
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"],
  });

  // Big Text Animation: Scales down staying centered, then fades out before cards appear
  const scale = useTransform(scrollYProgress, [0, 0.4], [4, 1]);
  const opacity = useTransform(scrollYProgress, [0, 0.1, 0.4, 0.5], [0, 1, 1, 0]);
  const textBlur = useTransform(scrollYProgress, [0, 0.2], ["10px", "0px"]);
  
  // Grid Animation: Fades in only after the text has disappeared
  const gridOpacity = useTransform(scrollYProgress, [0.55, 0.75], [0, 1]);
  const gridScale = useTransform(scrollYProgress, [0.55, 0.75], [0.95, 1]);
  const gridY = useTransform(scrollYProgress, [0.55, 0.8], [100, 0]);

  return (
    <div 
      id="features"
      ref={containerRef} 
      className="relative h-[400vh] bg-black" 
      data-theme="dark"
    >
      {/* Background Pattern */}
      <DotPattern
        glow={true}
        width={24}
        height={24}
        cr={1}
        className={cn("text-white/10 opacity-50")}
      />

      {/* Sticky Content Wrapper */}
      <div className="sticky top-0 h-screen w-full flex items-center justify-center overflow-hidden">
        
        {/* Cinematic Zooming Heading */}
        <motion.div
          style={{ scale, opacity, filter: `blur(${textBlur})`, willChange: "transform, opacity, filter" }}
          className="absolute z-20 pointer-events-none"
        >
          <h2 
            className="text-[15vw] tracking-tighter whitespace-nowrap uppercase font-bold"
            style={{ 
              fontFamily: "'Fjalla One', sans-serif",
              color: "#ff4d00",
              lineHeight: 1
            }}
          >
            Features
          </h2>
        </motion.div>

        {/* The Grid Content (Revealed later) */}
        <motion.div 
          style={{ opacity: gridOpacity, scale: gridScale, y: gridY, willChange: "transform, opacity" }}
          className="relative z-10 w-full max-w-7xl px-6"
        >
          <div className="mb-16 text-center">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              We Solve What Your Web Misses
            </h2>
            <p className="text-zinc-400 text-lg max-w-2xl mx-auto">
              AI-powered UX intelligence that analyzes user behavior, reveals hidden design problems, and generates actionable improvements.
            </p>
          </div>
          
          <ul className="grid grid-cols-1 grid-rows-none gap-4 md:grid-cols-12 md:grid-rows-3 lg:gap-4 xl:max-h-[34rem] xl:grid-rows-2">
            <GridItem
              area="md:[grid-area:1/1/2/7] xl:[grid-area:1/1/2/5]"
              icon={<Box className="h-4 w-4 text-white" />}
              title="AI-Powered Website Analysis"
              description="Our AI scans your website’s structure, layout, and design patterns to detect UX issues, weak visual hierarchy, and conversion blockers instantly."
            />
            <GridItem
              area="md:[grid-area:1/7/2/13] xl:[grid-area:1/5/2/9]"
              icon={<Settings className="h-4 w-4 text-white" />}
              title="AI-Powered Heatmaps"
              description="Automatically fine-tune your design elements for maximum engagement and performance."
            />
            <GridItem
              area="md:[grid-area:2/1/3/7] xl:[grid-area:1/9/2/13]"
              icon={<Lock className="h-4 w-4 text-white" />}
              title="Enterprise Security"
              description="Your designs and data are protected with industry-leading encryption and security standards."
            />
            <GridItem
              area="md:[grid-area:2/7/3/13] xl:[grid-area:2/1/3/5]"
              icon={<Sparkles className="h-4 w-4 text-white" />}
              title="Pixel Perfect Exports"
              description="Export your designs directly to React, Tailwind, or Figma with zero loss in quality."
            />
            <GridItem
              area="md:[grid-area:3/1/4/13] xl:[grid-area:2/5/3/13]"
              icon={<Search className="h-4 w-4 text-white" />}
              title="Smart Asset Search"
              description="Instantly find the perfect icons, images, and fonts from our curated library of millions of assets."
            />
          </ul>
        </motion.div>
      </div>
    </div>
  );
}

interface GridItemProps {
  area: string;
  icon: React.ReactNode;
  title: string;
  description: React.ReactNode;
}

const GridItem = ({ area, icon, title, description }: GridItemProps) => {
  return (
    <li className={cn("min-h-[14rem] list-none", area)}>
        {/* outer container for the effect */}
      <div className="relative h-full rounded-[1.25rem] border-[0.75px] border-zinc-800 p-2 md:rounded-[1.5rem] md:p-3">
        <GlowingEffect
          spread={40}
          glow={true}
          disabled={false}
          proximity={64}
          inactiveZone={0.01}
          borderWidth={3}
        />
        <div className="relative flex h-full flex-col justify-between gap-6 overflow-hidden rounded-xl border-[0.75px] bg-zinc-950 p-6 shadow-sm md:p-6">
          <div className="relative flex flex-1 flex-col justify-between gap-3">
            <div className="w-fit rounded-lg border-[0.75px] border-zinc-800 bg-zinc-900 p-2 text-white">
              {icon}
            </div>
            <div className="space-y-3">
              <h3 className="pt-0.5 text-xl leading-[1.375rem] font-semibold font-sans tracking-[-0.04em] md:text-2xl md:leading-[1.875rem] text-balance text-white">
                {title}
              </h3>
              <h2 className="font-sans text-sm leading-[1.125rem] md:text-base md:leading-[1.375rem] text-zinc-400">
                {description}
              </h2>
            </div>
          </div>
        </div>
      </div>
    </li>
  );
};

GlowingEffect.displayName = "GlowingEffect";

export { GlowingEffect };
